import {
  cache,
  getEnabledElementByViewportId,
  type Types,
} from '@cornerstonejs/core';
import type {
  RepresentationsData,
  Segmentation,
} from '../../../types/SegmentationStateTypes';
import type {
  LabelmapSegmentationDataStack,
  LabelmapSegmentationDataVolume,
} from '../../../types/LabelmapTypes';
import getViewportLabelmapRenderMode from '../helpers/getViewportLabelmapRenderMode';
import { ensureLabelmapState } from './normalizeLabelmapSegmentationData';
import { getLabelmaps } from './labelmapLayerStore';
import { getLabelmapForSegment } from './labelmapSegmentBindings';
import { getReferencedImageIdToCurrentImageIdMap } from './labelmapLegacyAdapter';
import { getLabelmapImageIdsForReferencedImageId } from './labelmapImageIdMapping';

type GetSegmentation = (segmentationId: string) => Segmentation | undefined;

class LabelmapImageReferenceResolver {
  private readonly getSegmentation: GetSegmentation;
  private readonly stackLabelmapImageIdReferenceMap = new Map<
    string,
    Map<string, string>
  >();
  private readonly labelmapImageIdReferenceMap = new Map<string, string[]>();
  // Index mapping each segmentationId to the set of keys it owns in
  // labelmapImageIdReferenceMap, so removeSegmentation can purge entries in
  // O(k) without scanning the whole map.
  private readonly keysBySegmentationId = new Map<string, Set<string>>();
  // Memo of already-performed isReferenceViewable scans. Each scan resolves
  // every labelmap imageId against the viewport's current image via full
  // metadata resolution (image plane, slice basis), which costs tens of ms
  // for multi-layer labelmaps - and the segmentation data-modified pipeline
  // re-enters this on EVERY brush/erase drag event. Scan keys include the
  // viewport id and a signature of the labelmap imageId set, so adding/removing
  // a layer (which changes the set) naturally forces a fresh scan.
  private readonly completedScanResultsBySegmentationId = new Map<
    string,
    Map<string, string | undefined>
  >();

  constructor(getSegmentation: GetSegmentation) {
    this.getSegmentation = getSegmentation;
  }

  reset(): void {
    this.stackLabelmapImageIdReferenceMap.clear();
    this.labelmapImageIdReferenceMap.clear();
    this.keysBySegmentationId.clear();
    this.completedScanResultsBySegmentationId.clear();
  }

  removeSegmentation(segmentationId: string): void {
    this.stackLabelmapImageIdReferenceMap.delete(segmentationId);
    this.completedScanResultsBySegmentationId.delete(segmentationId);
    const keys = this.keysBySegmentationId.get(segmentationId);
    if (keys) {
      for (const key of keys) {
        this.labelmapImageIdReferenceMap.delete(key);
      }
      this.keysBySegmentationId.delete(segmentationId);
    }
  }

  private setLabelmapImageIds(
    segmentationId: string,
    key: string,
    labelmapImageIds: string[]
  ): void {
    this.labelmapImageIdReferenceMap.set(key, labelmapImageIds);
    let keys = this.keysBySegmentationId.get(segmentationId);
    if (!keys) {
      keys = new Set();
      this.keysBySegmentationId.set(segmentationId, keys);
    }
    keys.add(key);
  }

  getLabelmapImageIds(representationData: RepresentationsData) {
    const labelmapData = representationData.Labelmap;
    let labelmapImageIds;

    if (!labelmapData) {
      return;
    }

    if (labelmapData?.labelmaps) {
      const imageIds = Object.values(labelmapData.labelmaps).flatMap(
        (layer) => {
          if (layer.imageIds?.length) {
            return layer.imageIds;
          }

          if (layer.volumeId) {
            return (cache.getVolume(layer.volumeId) as Types.IImageVolume)
              ?.imageIds;
          }

          return [];
        }
      );

      return Array.from(new Set(imageIds.filter(Boolean)));
    }

    if ((labelmapData as LabelmapSegmentationDataStack).imageIds) {
      labelmapImageIds = (labelmapData as LabelmapSegmentationDataStack)
        .imageIds;
    } else if ((labelmapData as LabelmapSegmentationDataVolume).volumeId) {
      const volumeId = (labelmapData as LabelmapSegmentationDataVolume)
        .volumeId;

      const volume = cache.getVolume(volumeId) as Types.IImageVolume;
      labelmapImageIds = volume.imageIds;
    }

    return labelmapImageIds;
  }

  getLabelmapImageIdsForImageId(imageId: string, segmentationId: string) {
    const segmentation = this.getSegmentation(segmentationId);
    if (!segmentation?.representationData?.Labelmap) {
      return;
    }

    ensureLabelmapState(segmentation);
    return getReferencedImageIdToCurrentImageIdMap(segmentation).get(imageId);
  }

  updateLabelmapSegmentationImageReferences(
    viewportId: string,
    segmentationId: string
  ): string | undefined {
    const segmentation = this.getSegmentation(segmentationId);
    if (!segmentation) {
      return;
    }

    if (!this.stackLabelmapImageIdReferenceMap.has(segmentationId)) {
      this.stackLabelmapImageIdReferenceMap.set(segmentationId, new Map());
    }

    const { representationData } = segmentation;
    if (!representationData.Labelmap) {
      return;
    }

    const labelmapImageIds = this.getLabelmapImageIds(representationData);
    const enabledElement = getEnabledElementByViewportId(viewportId);
    if (!enabledElement || !labelmapImageIds?.length) {
      return;
    }

    const viewport = enabledElement.viewport as Types.IStackViewport;
    const scanKey = this.generateScanKey('current', viewport, labelmapImageIds);
    if (scanKey && this.hasCompletedScan(segmentationId, scanKey)) {
      return this.getCompletedScanResult(segmentationId, scanKey);
    }

    const result = this.updateLabelmapSegmentationReferences(
      segmentationId,
      viewport,
      labelmapImageIds
    );

    if (scanKey) {
      this.markCompletedScan(segmentationId, scanKey, result);
    }

    return result;
  }

  /** Signature of one isReferenceViewable scan: the viewport's current image
   *  plus a cheap fingerprint of the labelmap imageId set (count + endpoints),
   *  so layer additions/removals invalidate naturally. */
  private generateScanKey(
    kind: 'current' | 'all',
    viewport: Types.IStackViewport,
    labelmapImageIds: string[]
  ): string | undefined {
    const referenceImageId =
      typeof viewport.getCurrentImageId === 'function'
        ? viewport.getCurrentImageId()
        : undefined;
    if (!referenceImageId) {
      return;
    }
    return `${kind}|${viewport.id}|${referenceImageId}|${
      labelmapImageIds.length
    }|${labelmapImageIds[0]}|${labelmapImageIds[labelmapImageIds.length - 1]}`;
  }

  private hasCompletedScan(segmentationId: string, scanKey: string): boolean {
    return !!this.completedScanResultsBySegmentationId
      .get(segmentationId)
      ?.has(scanKey);
  }

  private getCompletedScanResult(
    segmentationId: string,
    scanKey: string
  ): string | undefined {
    return this.completedScanResultsBySegmentationId
      .get(segmentationId)
      ?.get(scanKey);
  }

  private markCompletedScan(
    segmentationId: string,
    scanKey: string,
    result?: string
  ): void {
    let results = this.completedScanResultsBySegmentationId.get(segmentationId);
    if (!results) {
      results = new Map();
      this.completedScanResultsBySegmentationId.set(segmentationId, results);
    }
    results.set(scanKey, result);
  }

  getCurrentLabelmapImageIdsForViewport(
    viewportId: string,
    segmentationId: string
  ): string[] | undefined {
    const enabledElement = getEnabledElementByViewportId(viewportId);

    if (!enabledElement) {
      return;
    }

    const { viewport } = enabledElement;
    const viewportRenderMode = getViewportLabelmapRenderMode(viewport);

    if (
      viewportRenderMode !== 'image' ||
      typeof (viewport as Types.IStackViewport).getCurrentImageId !== 'function'
    ) {
      return;
    }

    const referenceImageId = (
      viewport as Types.IStackViewport
    ).getCurrentImageId();
    const segmentation = this.getSegmentation(segmentationId);
    if (!segmentation) {
      return;
    }

    ensureLabelmapState(segmentation);

    const viewportImageIds = (viewport as Types.IStackViewport).getImageIds();
    const currentIndex = viewportImageIds.indexOf(referenceImageId);
    const labelmapImageIds: string[] = [];

    getLabelmaps(segmentation).forEach((layer) => {
      const referencedLabelmapImageIds =
        getLabelmapImageIdsForReferencedImageId(layer, referenceImageId);

      if (referencedLabelmapImageIds.length) {
        labelmapImageIds.push(...referencedLabelmapImageIds);
        return;
      }

      if (currentIndex !== -1 && layer.imageIds?.[currentIndex]) {
        labelmapImageIds.push(layer.imageIds[currentIndex]);
        return;
      }

      layer.imageIds?.some((candidateImageId) => {
        const viewableImageId = (
          viewport as Types.IStackViewport
        ).isReferenceViewable(
          { referencedImageId: candidateImageId },
          { asOverlay: true }
        );

        if (viewableImageId) {
          labelmapImageIds.push(candidateImageId);
        }

        return !!viewableImageId;
      });
    });

    const resolvedImageIds = Array.from(new Set(labelmapImageIds));
    const key = this.generateMapKey({
      segmentationId,
      referenceImageId,
    });
    this.setLabelmapImageIds(segmentationId, key, resolvedImageIds);

    if (!this.stackLabelmapImageIdReferenceMap.has(segmentationId)) {
      this.stackLabelmapImageIdReferenceMap.set(segmentationId, new Map());
    }

    const activeSegmentIndex = Object.keys(segmentation.segments).find(
      (segmentIndex) => segmentation.segments[segmentIndex].active
    );
    const activeImageId = activeSegmentIndex
      ? getLabelmapForSegment(segmentation, Number(activeSegmentIndex))
          ?.imageIds?.[currentIndex]
      : undefined;

    this.stackLabelmapImageIdReferenceMap
      .get(segmentationId)
      .set(referenceImageId, activeImageId ?? resolvedImageIds[0]);

    return resolvedImageIds;
  }

  getCurrentLabelmapImageIdForViewport(
    viewportId: string,
    segmentationId: string
  ): string | undefined {
    const enabledElement = getEnabledElementByViewportId(viewportId);

    if (!enabledElement) {
      return;
    }

    const { viewport } = enabledElement;
    const viewportRenderMode = getViewportLabelmapRenderMode(viewport);

    if (
      viewportRenderMode !== 'image' ||
      typeof (viewport as Types.IStackViewport).getCurrentImageId !== 'function'
    ) {
      return;
    }

    const currentImageId = (
      viewport as Types.IStackViewport
    ).getCurrentImageId();
    const currentImageIds = this.getCurrentLabelmapImageIdsForViewport(
      viewportId,
      segmentationId
    );
    if (!currentImageIds?.length) {
      return;
    }

    const segmentation = this.getSegmentation(segmentationId);
    const currentIndex = (viewport as Types.IStackViewport)
      .getImageIds()
      .indexOf(currentImageId);
    const activeSegmentIndex = segmentation
      ? Object.keys(segmentation.segments).find(
          (segmentIndex) => segmentation.segments[segmentIndex].active
        )
      : undefined;
    const activeImageId =
      segmentation && activeSegmentIndex
        ? getLabelmapForSegment(segmentation, Number(activeSegmentIndex))
            ?.imageIds?.[currentIndex]
        : undefined;

    return activeImageId ?? currentImageIds[0];
  }

  getStackSegmentationImageIdsForViewport(
    viewportId: string,
    segmentationId: string
  ): string[] {
    const segmentation = this.getSegmentation(segmentationId);

    if (!segmentation) {
      return [];
    }

    this.updateAllLabelmapSegmentationImageReferences(
      viewportId,
      segmentationId
    );
    const enabledElement = getEnabledElementByViewportId(viewportId);
    const imageIds = enabledElement?.viewport.getImageIds?.() ?? [];
    const imageIdMap = getReferencedImageIdToCurrentImageIdMap(segmentation);

    return imageIds.flatMap((imageId) => imageIdMap.get(imageId) ?? []);
  }

  private updateLabelmapSegmentationReferences(
    segmentationId: string,
    viewport: Types.IStackViewport,
    labelmapImageIds: string[],
    updateCallback?: (
      viewport: Types.IStackViewport,
      segmentationId: string,
      labelmapImageIds: string[]
    ) => void
  ): string | undefined {
    const referenceImageId = viewport.getCurrentImageId();

    let viewableLabelmapImageIdFound = false;
    for (const labelmapImageId of labelmapImageIds) {
      const viewableImageId = viewport.isReferenceViewable(
        { referencedImageId: labelmapImageId },
        { asOverlay: true }
      );

      if (viewableImageId) {
        viewableLabelmapImageIdFound = true;
        this.stackLabelmapImageIdReferenceMap
          .get(segmentationId)
          .set(referenceImageId, labelmapImageId);
        this.updateLabelmapImageIdReferenceMap({
          segmentationId,
          referenceImageId,
          labelmapImageId,
        });
      }
    }

    updateCallback?.(viewport, segmentationId, labelmapImageIds);

    return viewableLabelmapImageIdFound
      ? this.stackLabelmapImageIdReferenceMap
          .get(segmentationId)
          .get(referenceImageId)
      : undefined;
  }

  /**
   * @deprecated Compatibility path for the old stack-labelmap API. New labelmap
   * layers should carry referencedImageIds so callers can use
   * getReferencedImageIdToCurrentImageIdMap without scanning every stack image
   * against every labelmap image.
   */
  private updateAllLabelmapSegmentationImageReferences(
    viewportId: string,
    segmentationId: string
  ): void {
    const segmentation = this.getSegmentation(segmentationId);
    if (!segmentation) {
      return;
    }

    if (!this.stackLabelmapImageIdReferenceMap.has(segmentationId)) {
      this.stackLabelmapImageIdReferenceMap.set(segmentationId, new Map());
    }

    const { representationData } = segmentation;
    if (!representationData.Labelmap) {
      return;
    }

    const labelmapImageIds = this.getLabelmapImageIds(representationData);
    const enabledElement = getEnabledElementByViewportId(viewportId);
    if (!enabledElement || !labelmapImageIds?.length) {
      return;
    }

    const stackViewport = enabledElement.viewport as Types.IStackViewport;

    const scanKey = this.generateScanKey(
      'all',
      stackViewport,
      labelmapImageIds
    );
    if (scanKey && this.hasCompletedScan(segmentationId, scanKey)) {
      return;
    }
    if (scanKey) {
      this.markCompletedScan(segmentationId, scanKey);
    }

    this.updateLabelmapSegmentationReferences(
      segmentationId,
      stackViewport,
      labelmapImageIds,
      (stackViewport, segmentationId, labelmapImageIds) => {
        const imageIds = stackViewport.getImageIds();
        imageIds.forEach((referenceImageId, index) => {
          for (const labelmapImageId of labelmapImageIds) {
            const viewableImageId = stackViewport.isReferenceViewable(
              { referencedImageId: labelmapImageId, sliceIndex: index },
              { asOverlay: true, withNavigation: true }
            );

            if (viewableImageId) {
              this.stackLabelmapImageIdReferenceMap
                .get(segmentationId)
                .set(referenceImageId, labelmapImageId);
              this.updateLabelmapImageIdReferenceMap({
                segmentationId,
                referenceImageId,
                labelmapImageId,
              });
            }
          }
        });
      }
    );
  }

  private updateLabelmapImageIdReferenceMap({
    segmentationId,
    referenceImageId,
    labelmapImageId,
  }): void {
    const key = this.generateMapKey({ segmentationId, referenceImageId });

    if (!this.labelmapImageIdReferenceMap.has(key)) {
      this.setLabelmapImageIds(segmentationId, key, [labelmapImageId]);
      return;
    }

    const currentValues = this.labelmapImageIdReferenceMap.get(key) ?? [];
    const newValues = Array.from(new Set([...currentValues, labelmapImageId]));
    this.setLabelmapImageIds(segmentationId, key, newValues);
  }

  private generateMapKey({ segmentationId, referenceImageId }) {
    return `${segmentationId}-${referenceImageId}`;
  }
}

export default LabelmapImageReferenceResolver;
